/**
 * Reservation Module — API Service v3.1
 *
 * Desktop (window.pms present):
 *   apiFetch → desktopApiFetch (SQLite reads / IPC mutations).
 *   NO IndexedDB.  NO offlineDB.  NO offlineCache.  NO offlineMutations.
 *   Offline ID generation is handled by httpToSyncEvent.ts / genTempId in Electron main.
 *
 * Browser (window.pms absent):
 *   apiFetch → IndexedDB cache (offlineDB) + offline mutation queue (offlineMutations).
 *   Sync engines (syncEngine, genericSyncEngine) drain the queue when back online.
 *
 * Token priority (most → least trusted):
 *   1. In-memory store  — set by postMessage from Main Hall (HOTELMIS_AUTH)
 *   2. localStorage     — set after staff login (staff-only flow)
 *   3. URL ?token=      — legacy fallback; token is moved to memory and stripped
 *
 * Tenant slug priority:
 *   1. URL ?tenant=     — authoritative when present
 *   2. localStorage     — persisted from previous visit / postMessage
 *   3. 'dev'            — local development fallback
 */

const API = '/api/v1';

// ─── In-memory auth store ──────────────────────────────────────────────────────
const _mem = {
  token:      null,
  tenantSlug: null,
  businessId: null,
  user:       null,
};

function decodeJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch { return null; }
}

/**
 * Returns true when the current token has less than `thresholdMs` until
 * expiry (default 2 hours).  Used by the proactive refresh cycle so we
 * always renew well before expiry — even if the user was idle all day.
 */
export function isTokenExpiringSoon(thresholdMs = 2 * 60 * 60 * 1000) {
  const token = getToken();
  if (!token) return false;
  const payload = decodeJwt(token);
  if (!payload?.exp) return false;
  const expiresInMs = payload.exp * 1000 - Date.now();
  return expiresInMs < thresholdMs;
}

function setMemToken(token, tenantSlug, businessId) {
  if (token)      { _mem.token      = token;      _mem.user = decodeJwt(token); }
  if (tenantSlug)   _mem.tenantSlug = tenantSlug;
  if (businessId)   _mem.businessId = businessId;
}

function bootstrapUrlToken() {
  const p         = new URLSearchParams(window.location.search);
  const urlTok    = p.get('token');
  const urlTenant = p.get('tenant');

  if (urlTenant) {
    localStorage.setItem('hotelmis_tenant_slug', urlTenant);
    _mem.tenantSlug = urlTenant;
  }

  if (urlTok) {
    setMemToken(urlTok, urlTenant ?? localStorage.getItem('hotelmis_tenant_slug'));
    p.delete('token');
    const clean = p.toString() ? `?${p}` : window.location.pathname;
    window.history.replaceState({}, '', clean);
  }
}

bootstrapUrlToken();

// ─── Desktop auth re-hydration on app reload ───────────────────────────────────
if (typeof window !== 'undefined' && window.pms) {
  Promise.resolve().then(() => {
    const token      = localStorage.getItem('hotelmis_token') || localStorage.getItem('hotelmis_admin_token');
    const tenantSlug = localStorage.getItem('hotelmis_tenant_slug');
    const propertyId = localStorage.getItem('hotelmis_property_id');
    if (token && tenantSlug) {
      window.pms.setAuth({ token, tenant: tenantSlug, propertyId: propertyId ?? '' })
        .catch(err => console.warn('[desktopRehydrate] pms.setAuth failed:', err));
    }
  });
}

export function receiveAuthFromParent({ token, tenantSlug, businessId }) {
  setMemToken(token, tenantSlug, businessId);
  if (tenantSlug) localStorage.setItem('hotelmis_tenant_slug', tenantSlug);
}

export function persistStaffAuth({ token, tenantSlug, propertyId }) {
  setMemToken(token, tenantSlug);
  localStorage.setItem('hotelmis_token',       token);
  localStorage.setItem('hotelmis_tenant_slug', tenantSlug);
  if (propertyId) localStorage.setItem('hotelmis_property_id', propertyId);

  if (typeof window !== 'undefined' && window.pms) {
    window.pms.setAuth({ token, tenant: tenantSlug, propertyId: propertyId ?? '' })
      .catch(err => console.warn('[persistStaffAuth] pms.setAuth failed:', err));
  }
}

export function getToken() {
  if (_mem.token) return _mem.token;
  const stored = localStorage.getItem('hotelmis_admin_token')
               || localStorage.getItem('hotelmis_token');
  if (stored) { _mem.token = stored; _mem.user = decodeJwt(stored); }
  return stored || null;
}

export function getTenantSlug() {
  if (_mem.tenantSlug && _mem.tenantSlug !== 'dev') return _mem.tenantSlug;

  const p    = new URLSearchParams(window.location.search);
  const slug = p.get('tenant')
    || localStorage.getItem('hotelmis_tenant_slug')
    || (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TENANT_SLUG)
    || 'dev';

  if (slug !== 'dev') _mem.tenantSlug = slug;
  return slug;
}

export function setTenantSlug(slug) {
  if (!slug) return;
  _mem.tenantSlug = slug;
  localStorage.setItem('hotelmis_tenant_slug', slug);
}

export function getCurrentUser() {
  if (_mem.user) return _mem.user;
  const t = getToken();
  if (t) { _mem.user = decodeJwt(t); return _mem.user; }
  return null;
}

export function getPropertyId()   { return localStorage.getItem('hotelmis_property_id'); }
export function setPropertyId(id) {
  if (!id) return;
  const prev = localStorage.getItem('hotelmis_property_id');
  localStorage.setItem('hotelmis_property_id', id);

  // Push to Electron main process immediately so SyncWorker gets the
  // propertyId as soon as loadProperty() resolves — not 5s later.
  // This is the fix for "missing propertyId" — without this,
  // startSnapshotSchedules() fires with propertyId='' and returns early.
  if (typeof window !== 'undefined' && window.pms?.setAuth) {
    const token  = localStorage.getItem('hotelmis_token')
                || localStorage.getItem('hotelmis_admin_token') || '';
    const tenant = localStorage.getItem('hotelmis_tenant_slug') || '';
    window.pms.setAuth({ token, tenant, propertyId: id })
      .then(() => {
        // Only trigger schedules if propertyId changed — avoids duplicate starts
        if (id !== prev) {
          window.pms.requestSync?.().catch(() => {});
        }
      })
      .catch(err => console.warn('[setPropertyId] pms.setAuth failed:', err));
  }
}

export function clearAuth() {
  _mem.token = _mem.tenantSlug = _mem.businessId = _mem.user = null;
  ['hotelmis_token', 'hotelmis_admin_token', 'hotelmis_tenant_slug', 'hotelmis_property_id']
    .forEach(k => localStorage.removeItem(k));
}

export async function silentRefreshToken() {
  const token = getToken();
  if (!token) return;
  try {
    const res = await fetch('/api/v1/auth/refresh', {
      method: 'POST',
      headers: {
        Authorization:   `Bearer ${token}`,
        'X-Tenant-Slug': getTenantSlug(),
        Accept:          'application/json',
      },
      // Don't let a slow network block more than 10 s
      signal: AbortSignal.timeout(10_000),
    });
    if (!res.ok) {
      // 401 = token already fully expired — nothing we can do silently
      if (res.status === 401) console.info('[Auth] Silent refresh skipped — token fully expired');
      return;
    }
    const json = await res.json();
    const newToken = json?.data?.token;
    if (!newToken) return;
    const key = localStorage.getItem('hotelmis_admin_token') ? 'hotelmis_admin_token' : 'hotelmis_token';
    localStorage.setItem(key, newToken);
    _mem.token = newToken;
    _mem.user  = decodeJwt(newToken);
    console.info('[Auth] Token refreshed silently — next expiry in 7 days');
  } catch (_) {
    // Network offline or timeout — safe to ignore; will retry on next interval
  }
}

export function buildStaffPortalUrl(tenantSlug) {
  const slug = tenantSlug || getTenantSlug();
  const base = `${window.location.origin}${window.location.pathname}`;
  return slug && slug !== 'dev' ? `${base}?tenant=${slug}` : base;
}

export function buildPublicBookingUrl(tenantSlug) {
  const slug = tenantSlug || getTenantSlug();
  const base = `${window.location.origin}${window.location.pathname}`;
  return slug && slug !== 'dev' ? `${base}?book=1&tenant=${slug}` : `${base}?book=1`;
}

// ─── Backend reachability (browser-only) ──────────────────────────────────────
// In desktop mode, connectivity state is managed by desktopApiFetch.js via
// the ConnectivityMonitor IPC subscription — we do not duplicate it here.
let _backendReachable = true;

if (!window.pms) {
  window.addEventListener('hotelmis:backend:online',  () => { _backendReachable = true;  });
  window.addEventListener('hotelmis:backend:offline', () => { _backendReachable = false; });
  window.addEventListener('offline',                  () => { _backendReachable = false; });
}

function markUnreachable() {
  if (_backendReachable) {
    window.dispatchEvent(new CustomEvent('hotelmis:backend:offline'));
  }
  _backendReachable = false;
}

function markReachable() {
  if (!_backendReachable) {
    _backendReachable = true;
    window.dispatchEvent(new CustomEvent('hotelmis:backend:online'));
  }
  _backendReachable = true;
}

// Desktop build: ConnectivityMonitor in Electron main handles probing.
export function startOnlineHealthCheck() {
  // Browser path: probe the backend every 30 s so markReachable/markUnreachable
  // fire automatically and the "Retry Connection" button works without a manual click.
  if (typeof window === 'undefined' || window.pms) return; // desktop: no-op
  const probe = async () => {
    try {
      // FIX: use /module/health (public, DB-aware endpoint) not /health (non-existent
      // — that path returns 404 which incorrectly calls markUnreachable() on every probe).
      const res = await fetch(`${API}/module/health`, {
        signal: AbortSignal.timeout(5_000),
      });
      // 401/403 = server reachable, just rejecting the auth token
      if (res.ok || res.status === 401 || res.status === 403) markReachable();
      else markUnreachable();
    } catch { markUnreachable(); }
  };
  probe();
  setInterval(probe, 30_000);
}

// Auto-start health probing in browser mode immediately when this module is loaded.
// Guards against window.pms (desktop) and SSR — pure no-op in those environments.
if (typeof window !== 'undefined' && !window.pms) {
  // Defer to next tick so callers can add their own event listeners first.
  Promise.resolve().then(() => startOnlineHealthCheck());
}

export function isBackendReachable() {
  // Desktop: trust the Electron ConnectivityMonitor (it manages its own _isOnline)
  if (typeof window !== 'undefined' && window.pms) return true;
  // Browser: use the local flag updated by markReachable/markUnreachable
  return _backendReachable;
}

// ─── Fetch timeout (browser path) ────────────────────────────────────────────
// FIX: increased from 10 s to 20 s — first-time schema provisioning (Liquibase)
// can take 15-30 s on a cold DB. 10 s caused spurious "Request timed out" errors
// on the very first fetchProperties() call after a new tenant's initial login.
const FETCH_TIMEOUT_MS = 20_000;

async function fetchWithTimeout(url, opts = {}) {
  const ctrl = new AbortController();
  const tid  = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...opts, signal: ctrl.signal });
  } catch (err) {
    if (err.name === 'AbortError') {
      const e = new TypeError(`Request timed out after ${FETCH_TIMEOUT_MS / 1000}s`);
      e.cause = err;
      throw e;
    }
    throw err;
  } finally {
    clearTimeout(tid);
  }
}

// ─── Browser fetch (window.pms absent) ───────────────────────────────────────
/**
 * Direct HTTP call to the real backend — used only in browser context.
 * Mirrors the header/auth/unwrap logic of desktopApiFetch.onlineFetch()
 * so both paths return the same shape to every caller in the app.
 */
async function browserApiFetch(path, opts = {}) {
  const token  = getToken();
  const tenant = getTenantSlug();
  const pid    = getPropertyId();

  // VITE_API_URL is injected at build time; empty = same-origin in dev/proxy setups
  const base = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
  const url  = `${base}${API}${path}`;

  const headers = {
    'Content-Type':  'application/json',
    Accept:          'application/json',
    'X-Tenant-Slug': tenant,
    ...(opts.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (pid)   headers['X-Property-Id'] = pid;

  let res;
  try {
    res = await fetchWithTimeout(url, { ...opts, headers });
  } catch (err) {
    // Network-level failure — no connection, CORS, DNS, timeout
    markUnreachable();
    throw err;
  }

  if (res.status === 401) {
    // Read the body once up front so we can branch on errorCode before
    // deciding whether a silent refresh is even worth attempting.
    const bodyText = await res.text().catch(() => '');
    const bodyJson = (() => { try { return JSON.parse(bodyText); } catch { return null; } })();

    // Fully expired token — the backend has told us explicitly, so don't
    // waste a round-trip on silentRefreshToken() (it would just 401 again).
    // Go straight to a silent logout + redirect: no toast, no thrown error,
    // matching the axios interceptor pattern used elsewhere in the app.
    if (bodyJson?.errorCode === 'TOKEN_EXPIRED') {
      clearAuth();
      if (typeof window !== 'undefined') window.location.href = '/login';
      return null;
    }

    // Any other 401 — attempt one silent refresh before giving up. Covers the
    // edge case where the token expired between the last refresh cycle and
    // this request but the backend hasn't tagged it as TOKEN_EXPIRED.
    try {
      await silentRefreshToken();
      const retryToken = getToken();
      if (retryToken) {
        // Retry the original request once with the new token
        const retryHeaders = { ...headers, Authorization: `Bearer ${retryToken}` };
        const retry = await fetchWithTimeout(url, { ...opts, headers: retryHeaders });
        if (retry.ok) {
          markReachable();
          const retryText = await retry.text();
          if (!retryText) return null;
          const retryJson = JSON.parse(retryText);
          return (retryJson && typeof retryJson === 'object' && 'data' in retryJson)
            ? retryJson.data : retryJson;
        }
      }
    } catch (_) { /* refresh failed — fall through to logout */ }

    // Refresh failed or wasn't possible — silent logout + redirect, same as
    // the TOKEN_EXPIRED branch above (no thrown error, no toast).
    clearAuth();
    if (typeof window !== 'undefined') window.location.href = '/login';
    return null;
  }

  if (!res.ok) {
    if (res.status >= 500) markUnreachable();
    const text = await res.text().catch(() => '');
    const data = (() => { try { return JSON.parse(text); } catch { return null; } })();
    throw new Error(data?.message || data?.error || `Server error ${res.status}`);
  }

  markReachable();
  const text = await res.text();
  if (!text) return null;
  const json = JSON.parse(text);
  // Unwrap standard ApiResponse envelope: { success: true, data: ... }
  return (json && typeof json === 'object' && 'data' in json) ? json.data : json;
}

// ─── Core fetch — routes between desktop (SQLite/IPC) and browser (HTTP) ──────
/**
 * FIX: "Cannot read properties of undefined (reading 'queryTable')"
 *
 * The previous version unconditionally imported desktopApiFetch and delegated
 * every call to it.  desktopApiFetch calls window.pms.queryTable(...) on every
 * GET — in the browser window.pms is undefined → TypeError on first API call.
 *
 * Now apiFetch checks for window.pms at call time:
 *   Desktop (window.pms present) → desktopApiFetch (SQLite reads / IPC queue)
 *   Browser (window.pms absent)  → browserApiFetch (real HTTP to backend)
 */
async function apiFetch(path, opts = {}) {
  if (typeof window !== 'undefined' && window.pms) {
    const { desktopApiFetch } = await import('../platform/desktopApiFetch.js');
    return desktopApiFetch(path, opts);
  }
  return browserApiFetch(path, opts);
}

// findCachedConflicts: not applicable on desktop (SQLite handles conflict checks)
export async function findCachedConflicts() { return []; }

// getCachedProperties: Desktop → read from SQLite; Browser → no local cache
// (return [] so loadProperty() immediately falls through to fetchProperties()).
export async function getCachedProperties() {
  if (typeof window === 'undefined' || !window.pms) return []; // browser: no SQLite
  try {
    const { desktopApiFetch } = await import('../platform/desktopApiFetch.js');
    const data = await desktopApiFetch('/properties');
    if (!data) return [];
    const list = Array.isArray(data) ? data : (data?.content ?? []);
    // On first boot SQLite may be empty even though the IPC is working fine.
    // Fall back to a direct pms.queryTable call with no filter to be sure.
    if (list.length === 0 && window.pms?.queryTable) {
      const r = await window.pms.queryTable('properties', {});
      if (r?.success && r.data?.length > 0) return r.data;
    }
    return list;
  } catch { return []; }
}


// ─── Properties ───────────────────────────────────────────────────────────────
export const fetchProperties    = ()       => apiFetch('/properties');
export const fetchProperty      = (id)     => apiFetch(`/properties/${id}`);
export const createProperty     = (d)      => apiFetch('/properties', { method: 'POST', body: JSON.stringify(d) });
export const updateProperty     = (id, d)  => apiFetch(`/properties/${id}`, { method: 'PUT', body: JSON.stringify(d) });

// ─── Room Types ───────────────────────────────────────────────────────────────
export const fetchRoomTypes  = (pid)     => apiFetch(`/properties/${pid}/room-types`);
export const createRoomType  = (pid, d)  => apiFetch(`/properties/${pid}/room-types`, { method: 'POST', body: JSON.stringify(d) });
export const updateRoomType  = (id, d)   => apiFetch(`/room-types/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const deleteRoomType  = (id)      => apiFetch(`/room-types/${id}`, { method: 'DELETE' });

// ─── Rooms ────────────────────────────────────────────────────────────────────
export const fetchRooms       = (pid)    => apiFetch(`/properties/${pid}/rooms`);
export const createRoom       = (pid, d) => apiFetch(`/properties/${pid}/rooms`, { method: 'POST', body: JSON.stringify(d) });
export const updateRoom       = (id, d)  => apiFetch(`/rooms/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const updateRoomStatus = (id, st) => apiFetch(`/rooms/${id}/status?status=${st}`, { method: 'PATCH' });
export const deleteRoom       = (id)     => apiFetch(`/rooms/${id}`, { method: 'DELETE' });

// ─── Availability ─────────────────────────────────────────────────────────────
export const checkAvailability = (pid, arrival, dep, adults = 1, children = 0) =>
  apiFetch(`/availability?propertyId=${pid}&arrival=${arrival}&departure=${dep}&adults=${adults}&children=${children}`);

// ─── Rate Plans ───────────────────────────────────────────────────────────────
export const fetchRatePlans  = (pid)    => apiFetch(`/rate-plans?propertyId=${pid}`);
export const createRatePlan  = (d)      => apiFetch('/rate-plans', { method: 'POST', body: JSON.stringify(d) });
export const updateRatePlan  = (id, d)  => apiFetch(`/rate-plans/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const deleteRatePlan  = (id)     => apiFetch(`/rate-plans/${id}`, { method: 'DELETE' });

// ─── Corporate Accounts (companies with negotiated contracts) ─────────────────
export const fetchCorporateAccounts = (pid, activeOnly = false) =>
  apiFetch(`/corporate-accounts?propertyId=${pid}${activeOnly ? '&activeOnly=true' : ''}`);
export const searchCorporateAccounts = (pid, q) =>
  apiFetch(`/corporate-accounts?propertyId=${pid}&search=${encodeURIComponent(q)}`);
export const fetchCorporateAccount  = (id)     => apiFetch(`/corporate-accounts/${id}`);
export const createCorporateAccount = (d)      => apiFetch('/corporate-accounts', { method: 'POST', body: JSON.stringify(d) });
export const updateCorporateAccount = (id, d)  => apiFetch(`/corporate-accounts/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const deactivateCorporateAccount = (id) => apiFetch(`/corporate-accounts/${id}`, { method: 'DELETE' });

export const fetchCorporateTariffs  = (accountId)     => apiFetch(`/corporate-accounts/${accountId}/tariffs`);
export const addCorporateTariff     = (accountId, d)  => apiFetch(`/corporate-accounts/${accountId}/tariffs`, { method: 'POST', body: JSON.stringify(d) });
export const updateCorporateTariff  = (tariffId, d)   => apiFetch(`/corporate-accounts/tariffs/${tariffId}`, { method: 'PUT', body: JSON.stringify(d) });
export const deleteCorporateTariff  = (tariffId)      => apiFetch(`/corporate-accounts/tariffs/${tariffId}`, { method: 'DELETE' });
export const resolveCorporateTariff = (accountId, serviceType, roomTypeId) =>
  apiFetch(`/corporate-accounts/${accountId}/tariffs/resolve?serviceType=${encodeURIComponent(serviceType)}${roomTypeId ? `&roomTypeId=${roomTypeId}` : ''}`);
export const fetchCorporateAccountGuests = (accountId) => apiFetch(`/corporate-accounts/${accountId}/guests`);

// ─── Reservations ─────────────────────────────────────────────────────────────
export const PAGE_SIZE             = 10;
export const RESERVATION_LIST_SIZE = PAGE_SIZE;
/**
 * FIX: added optional `category` param (7th arg) — sent as a `category`
 * query param alongside `status`/`search` so the reservation list can be
 * filtered by category server-side. Existing callers that don't pass it
 * are unaffected since it's appended at the end and only set when truthy
 * and not 'all' (same pattern as `status`).
 */
export const fetchReservations  = (pid, status, search, page = 0, size = PAGE_SIZE, category) => {
  const p = new URLSearchParams({ propertyId: pid, page, size });
  if (status && status !== 'all') p.set('status', status);
  if (search?.trim()) p.set('search', search.trim());
  if (category && category !== 'all') p.set('category', category);
  return apiFetch(`/reservations?${p}`);
};
export const fetchReservation   = (id)        => apiFetch(`/reservations/${id}`);
export const createReservation  = (d)         => apiFetch('/reservations', { method: 'POST', body: JSON.stringify(d) });
export const updateReservation  = (id, d)     => apiFetch(`/reservations/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const confirmReservation = (id)        => apiFetch(`/reservations/${id}/confirm`, { method: 'POST' });
export const checkIn            = (id, rid)   => apiFetch(`/reservations/${id}/check-in${rid ? '?roomId=' + rid : ''}`, { method: 'POST' });
export const checkOut           = (id, skipBalanceCheck = false) =>
  apiFetch(`/reservations/${id}/check-out${skipBalanceCheck ? '?skipBalanceCheck=true' : ''}`, { method: 'POST' });
export const cancelReservation  = (id, r)     => apiFetch(`/reservations/${id}/cancel${r ? '?reason=' + encodeURIComponent(r) : ''}`, { method: 'POST' });

/**
 * Preview or confirm a room change (upgrade / downgrade / same-price transfer).
 * Pass dryRun=true for a financial preview without any DB changes.
 * Works for both confirmed and checked-in reservations.
 */
export const previewRoomChange = (reservationId, newRoomId) =>
  apiFetch(`/reservations/${reservationId}/change-room?dryRun=true`, {
    method: 'POST',
    body: JSON.stringify({ newRoomId }),
  });
export const changeRoom = (reservationId, newRoomId, reason = '') =>
  apiFetch(`/reservations/${reservationId}/change-room`, {
    method: 'POST',
    body: JSON.stringify({ newRoomId, reason }),
  });

/**
 * Returns the SessionPlannedServices for the corporate session guest
 * linked to this reservation — used by InvoicePrint to show correct
 * qty / Days / Unit Price columns for corporate folio invoices.
 * Returns [] if the reservation is not part of a corporate session.
 */
export const fetchReservationSessionServices = (reservationId) =>
  apiFetch(`/reservations/${reservationId}/session-services`);
export const markNoShow         = (id, feeAmount, reason) => apiFetch(`/reservations/${id}/no-show`, {
  method: 'POST',
  body: JSON.stringify({ feeAmount: feeAmount ?? null, reason: reason ?? null }),
});
export const extendStay         = (id, date)  => apiFetch(`/reservations/${id}/extend?newDeparture=${date}`, { method: 'POST' });
export const fetchArrivals      = (pid, d)    => apiFetch(`/reservations/arrivals?propertyId=${pid}${d ? '&date=' + d : ''}`);
export const fetchDepartures    = (pid, d)    => apiFetch(`/reservations/departures?propertyId=${pid}${d ? '&date=' + d : ''}`);
export const fetchInHouse       = (pid)       => apiFetch(`/reservations/inhouse?propertyId=${pid}`);
export const fetchTimeline      = (pid, f, t) => apiFetch(`/reservations/timeline?propertyId=${pid}&from=${f}&to=${t}`);

// ─── Additional people included under one individual reservation ──────────────
// The focal/primary guest stays on the reservation itself (guestName, etc.);
// these manage the additional people recorded under the SAME reservation/room.
export const fetchReservationGuests = (reservationId) =>
  apiFetch(`/reservations/${reservationId}/guests`);
export const addReservationGuest    = (reservationId, d) =>
  apiFetch(`/reservations/${reservationId}/guests`, { method: 'POST', body: JSON.stringify(d) });
export const updateReservationGuest = (reservationId, guestRowId, d) =>
  apiFetch(`/reservations/${reservationId}/guests/${guestRowId}`, { method: 'PUT', body: JSON.stringify(d) });
export const removeReservationGuest = (reservationId, guestRowId) =>
  apiFetch(`/reservations/${reservationId}/guests/${guestRowId}`, { method: 'DELETE' });

// ─── Folio ────────────────────────────────────────────────────────────────────
export const fetchFolio        = (id)           => apiFetch(`/reservations/${id}/folio`);
export const postCharge        = (id, d)        => apiFetch(`/reservations/${id}/charges`, { method: 'POST', body: JSON.stringify(d) });
export const voidCharge        = (cid, by)      => apiFetch(`/charges/${cid}${by ? '?voidedBy=' + by : ''}`, { method: 'DELETE' });
export const recordPayment     = (id, d)        => apiFetch(`/reservations/${id}/payments`, { method: 'POST', body: JSON.stringify(d) });
export const reversePayment    = (pid, reason)  => apiFetch(`/payments/${pid}/reverse`, { method: 'POST', body: JSON.stringify({ reason }) });
export const refundPayment     = (rid, d)       => apiFetch(`/reservations/${rid}/refund`, { method: 'POST', body: JSON.stringify(d) });
export const transferCharge    = (cid, toId)    => apiFetch(`/charges/${cid}/transfer?toReservation=${toId}`, { method: 'POST' });
export const splitFolio        = (id, d)        => apiFetch(`/reservations/${id}/folio/split`, { method: 'POST', body: JSON.stringify(d) });
export const fetchFolioReceipt = (id)           => apiFetch(`/reservations/${id}/folio/receipt`);

// ─── Housekeeping ─────────────────────────────────────────────────────────────
export const fetchHKTasks  = (pid, date) => apiFetch(`/housekeeping/tasks?propertyId=${pid}${date ? '&date=' + date : ''}`);
export const createHKTask  = (d)         => apiFetch('/housekeeping/tasks', { method: 'POST', body: JSON.stringify(d) });
export const updateHKTask  = (id, st, n) => apiFetch(`/housekeeping/tasks/${id}?status=${st}${n ? '&notes=' + encodeURIComponent(n) : ''}`, { method: 'PATCH' });
export const assignHKTask  = (id, staff) => apiFetch(`/housekeeping/tasks/${id}/assign?staffId=${staff}`, { method: 'POST' });
export const fetchRoomBoard = (pid)      => apiFetch(`/housekeeping/room-status?propertyId=${pid}`);
export const fetchHKStats  = (pid, d)    => apiFetch(`/housekeeping/stats?propertyId=${pid}${d ? '&date=' + d : ''}`);
export const autoAssignHK  = (pid)       => apiFetch(`/housekeeping/auto-assign?propertyId=${pid}`, { method: 'POST' });

// ─── Guests ───────────────────────────────────────────────────────────────────
export const fetchGuests       = (pid, s, page = 0, size = PAGE_SIZE) => apiFetch(`/guests?propertyId=${pid}&page=${page}&size=${size}${s ? '&search=' + encodeURIComponent(s) : ''}`);
export const fetchGuest        = (id)                => apiFetch(`/guests/${id}`);
export const createGuest       = (d)                 => apiFetch('/guests', { method: 'POST', body: JSON.stringify(d) });
export const updateGuest       = (id, d)             => apiFetch(`/guests/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const fetchGuestHistory = (id)                => apiFetch(`/guests/${id}/history`);
export const fetchGuestFolios  = (id)                => apiFetch(`/guests/${id}/folios`);
export const blacklistGuest    = (id, r)             => apiFetch(`/guests/${id}/blacklist?reason=${encodeURIComponent(r)}`, { method: 'POST' });

// ─── Staff ────────────────────────────────────────────────────────────────────
export const fetchStaff             = (pid)   => apiFetch(`/staff?propertyId=${pid}`);
export const fetchStaffMember       = (id)    => apiFetch(`/staff/${id}`);
export const createStaff            = (d)     => apiFetch('/staff', { method: 'POST', body: JSON.stringify(d) });
export const updateStaff            = (id, d) => apiFetch(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const toggleStaff            = (id)    => apiFetch(`/staff/${id}/toggle-active`, { method: 'PATCH' });
export const resetStaffPassword     = (id)    => apiFetch(`/staff/${id}/reset-password`, { method: 'POST' });
export const fetchStaffRoles        = ()      => apiFetch('/staff/roles');
export const fetchStaffAuditLogs    = (staffId) => apiFetch(`/audit-logs/staff/${staffId}`);
export const fetchPropertyAuditLogs = (pid, page = 0) => apiFetch(`/audit-logs?propertyId=${pid}&page=${page}&size=50`);
export const truncateAuditLogs      = (pid)   => apiFetch(`/audit-logs/truncate?propertyId=${pid}`, { method: 'POST' });

export const refreshTenantSchema = () => apiFetch('/module/refresh-schema', { method: 'POST' });
export const fetchMyProfile      = ()      => apiFetch('/staff/me');
export const updateMyProfile     = (d)     => apiFetch('/staff/me', { method: 'PUT', body: JSON.stringify(d) });
export const changeMyPassword    = (d)     => apiFetch('/staff/me/password', { method: 'POST', body: JSON.stringify(d) });

// ─── Email Config ─────────────────────────────────────────────────────────────
export const fetchEmailConfig = (pid)    => apiFetch(`/email-config?propertyId=${pid}`);
export const saveEmailConfig  = (pid, d) => apiFetch(`/email-config?propertyId=${pid}`, { method: 'PUT', body: JSON.stringify(d) });
export const testEmailConfig  = (pid, d) => apiFetch(`/email-config/test?propertyId=${pid}`, { method: 'POST', body: JSON.stringify(d) });

// ─── Online Booking / Channels ────────────────────────────────────────────────
export const fetchChannels       = (pid)    => apiFetch(`/channels?propertyId=${pid}`);
export const updateChannel       = (id, d)  => apiFetch(`/channels/${id}`, { method: 'PUT', body: JSON.stringify(d) });
export const fetchBookingWidget  = (pid)    => apiFetch(`/booking/widget?propertyId=${pid}`);
export const updateBookingWidget = (pid, d) => apiFetch(`/booking/widget?propertyId=${pid}`, { method: 'PUT', body: JSON.stringify(d) });

// ─── Financial Statement ──────────────────────────────────────────────────────
export const fetchFinancialStatement   = (pid, f, t) => apiFetch(`/financial/statement?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchFinancialOutstanding = (pid)        => apiFetch(`/financial/outstanding?propertyId=${pid}`);
export const fetchFinancialComparison  = (pid, f, t)  => apiFetch(`/financial/comparison?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchFinancialTax         = (pid, f, t)  => apiFetch(`/financial/tax?propertyId=${pid}&from=${f}&to=${t}`);

// ─── Reports ──────────────────────────────────────────────────────────────────
export const fetchRevenueSummary    = (pid, f, t) => apiFetch(`/reports/revenue?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchOccupancyReport   = (pid, f, t) => apiFetch(`/reports/occupancy?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchArrivalManifest   = (pid, f, t) => apiFetch(`/reports/manifest/arrivals?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchDepartureManifest = (pid, f, t) => apiFetch(`/reports/manifest/departures?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchChannelReport     = (pid, f, t) => apiFetch(`/reports/channels?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchFolioReport       = (pid, f, t) => apiFetch(`/reports/folio?propertyId=${pid}&from=${f}&to=${t}`);
export const fetchHKReport          = (pid, f, t) => apiFetch(`/reports/housekeeping?propertyId=${pid}&from=${f}&to=${t}`);
/** District Guest Report — all in-house guests for a single date, or a from/to date range (YYYY-MM-DD) */
export const fetchDistrictReport = (propertyId, from, to) =>
  apiFetch(`/reservations/district-report?propertyId=${propertyId}&from=${from}${to ? `&to=${to}` : ''}`);


// ─── Dashboard ────────────────────────────────────────────────────────────────
export const fetchDashboardStats   = (pid)                    => apiFetch(`/dashboard/stats?propertyId=${pid}`);
export const fetchDashboardRevenue = (pid, days = DASHBOARD_REVENUE_DAYS) => apiFetch(`/dashboard/revenue?propertyId=${pid}&days=${days}`);

// ─── Notifications ────────────────────────────────────────────────────────────
export const fetchNotifications = (pid) => apiFetch(`/notifications?propertyId=${pid}`);

// ─── Module ───────────────────────────────────────────────────────────────────
export const fetchManifest      = () => apiFetch('/module/manifest');
export const fetchTenantModules = () => apiFetch('/modules/tenant');

// ─── Auth endpoints ───────────────────────────────────────────────────────────
export async function loginStaff(email, password, tenantSlug) {
  // FIX: use the same VITE_API_URL base as browserApiFetch so production deployments
  // with a separate backend host work correctly. Previously used a bare '/api/v1/auth/login'
  // (same-origin), which diverged from all other apiFetch calls when VITE_API_URL is set.
  const base = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
  const res  = await fetch(`${base}${API}/auth/login`, {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': tenantSlug },
    body:    JSON.stringify({ email, password }),
  });
  const json = await res.json().catch(() => ({}));
  return { ...json, status: res.status };
}

export async function checkTenantSubscription(tenantSlug) {
  const base = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || '';
  const res  = await fetch(`${base}${API}/auth/tenant-check`, {
    headers: {
      'X-Tenant-Slug': tenantSlug,
      Authorization:   `Bearer ${getToken() || ''}`,
    },
  });
  const json = await res.json().catch(() => ({}));
  return { ...json, status: res.status };
}

// ─── Room Type Images ──────────────────────────────────────────────────────────
export async function uploadRoomTypeImages(roomTypeId, files) {
  const fd = new FormData();
  files.forEach(f => fd.append('files', f));
  const res = await fetch(`${API}/room-types/${roomTypeId}/images`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
    body: fd,
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Upload failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

export async function deleteRoomTypeImage(roomTypeId, filename) {
  const res = await fetch(`${API}/room-types/${roomTypeId}/images/${encodeURIComponent(filename)}`, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Delete failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

export async function reorderRoomTypeImages(roomTypeId, orderedUrls) {
  const res = await fetch(`${API}/room-types/${roomTypeId}/images/reorder`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
    body:    JSON.stringify({ urls: orderedUrls }),
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Reorder failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

// ─── Room Images ───────────────────────────────────────────────────────────────
export async function uploadRoomImages(roomId, files) {
  const fd = new FormData();
  files.forEach(f => fd.append('files', f));
  const res = await fetch(`${API}/rooms/${roomId}/images`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
    body: fd,
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Upload failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

export async function fetchRoomImages(roomId) {
  try {
    return await apiFetch(`/rooms/${roomId}/images`);
  } catch {
    return { images: [], count: 0, roomId };
  }
}

export async function deleteRoomImage(roomId, filename) {
  const res = await fetch(`${API}/rooms/${roomId}/images/${encodeURIComponent(filename)}`, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Delete failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

export async function reorderRoomImages(roomId, orderedUrls) {
  const res = await fetch(`${API}/rooms/${roomId}/images/reorder`, {
    method:  'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
    body:    JSON.stringify({ urls: orderedUrls }),
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Reorder failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

// ─── Property Logo ──────────────────────────────────────────────────────────
export async function uploadPropertyLogo(propertyId, file) {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch(`${API}/properties/${propertyId}/logo`, {
    method:  'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
    body: fd,
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Upload failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}

export async function deletePropertyLogo(propertyId) {
  const res = await fetch(`${API}/properties/${propertyId}/logo`, {
    method:  'DELETE',
    headers: { Authorization: `Bearer ${getToken()}`, 'X-Tenant-Slug': getTenantSlug() },
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.message || `Delete failed HTTP ${res.status}`); }
  const json = await res.json();
  return json.data ?? json;
}


export async function fetchSubscriptionStatus() {
  // Call the reservation backend's proxied tunnel endpoint.
  // This is a lightweight call — the backend serves from its 5-minute cache.
  const token  = getToken();
  const tenant = getTenantSlug();
  if (!token || !tenant) return null;
 
  const res = await fetch('/api/v1/module/subscription-status', {
    headers: {
      Authorization:   `Bearer ${token}`,
      'X-Tenant-Slug': tenant,
      Accept:          'application/json',
    },
    signal: AbortSignal.timeout(8_000),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json?.data ?? json;
}
 
export async function refreshSubscriptionStatus() {
  // POST to evict the server-side cache and re-fetch from Main Hall.
  // Called by the SubscriptionWatchdog "Already renewed?" button.
  const token  = getToken();
  const tenant = getTenantSlug();
  if (!token || !tenant) return null;
 
  const res = await fetch('/api/v1/module/subscription-refresh', {
    method:  'POST',
    headers: {
      Authorization:   `Bearer ${token}`,
      'X-Tenant-Slug': tenant,
      Accept:          'application/json',
    },
    signal: AbortSignal.timeout(10_000),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json?.data ?? json;
}


// Sessions — filtered by propertyId + optional accountId
export const fetchCorporateSessions = (pid, status, accountId) => {
  let url = `/corporate-sessions?propertyId=${pid}`;
  if (status && status !== 'all') url += `&status=${encodeURIComponent(status)}`;
  if (accountId) url += `&corporateAccountId=${encodeURIComponent(accountId)}`;
  return apiFetch(url);
};
export const fetchCorporateSession       = (id)    => apiFetch(`/corporate-sessions/${id}`);
export const fetchCorporateSessionDetail = (id)    => apiFetch(`/corporate-sessions/${id}/detail`);
export const createCorporateSession      = (d)     => apiFetch('/corporate-sessions', { method:'POST', body:JSON.stringify(d) });
export const updateCorporateSession      = (id, d) => apiFetch(`/corporate-sessions/${id}`, { method:'PUT', body:JSON.stringify(d) });

// Rooms available for a given session + room type (respects the session's date range)
export const fetchAvailableRoomsForSession = (sessionId, roomTypeId) =>
  apiFetch(`/corporate-sessions/${sessionId}/available-rooms?roomTypeId=${roomTypeId}`);

// Session Tariffs
export const fetchSessionTariffs = (sessionId)    => apiFetch(`/corporate-sessions/${sessionId}/tariffs`);
// Conference schedule: one package on one date with that day's participant count
export const saveConferenceDay   = (sessionId, d)    => apiFetch(`/corporate-sessions/${sessionId}/conference-days`, { method:'POST', body:JSON.stringify(d) });
export const bulkConferenceDays  = (sessionId, d)    => apiFetch(`/corporate-sessions/${sessionId}/conference-days/bulk`, { method:'POST', body:JSON.stringify(d) });
export const deleteConferenceDay = (sessionId, dayId) => apiFetch(`/corporate-sessions/${sessionId}/conference-days/${dayId}`, { method:'DELETE' });
export const saveSessionTariff   = (sessionId, d) => apiFetch(`/corporate-sessions/${sessionId}/tariffs`, { method:'POST', body:JSON.stringify(d) });
export const deleteSessionTariff = (tariffId)     => apiFetch(`/corporate-sessions/tariffs/${tariffId}`, { method:'DELETE' });

// Session Guests
export const fetchSessionGuests = (sessionId)    => apiFetch(`/corporate-sessions/${sessionId}/guests`);
export const addSessionGuest    = (sessionId, d) => apiFetch(`/corporate-sessions/${sessionId}/guests`, { method:'POST', body:JSON.stringify(d) });
export const updateSessionGuest = (guestId, d)   => apiFetch(`/corporate-sessions/guests/${guestId}`, { method:'PUT', body:JSON.stringify(d) });
export const removeSessionGuest = (guestId)      => apiFetch(`/corporate-sessions/guests/${guestId}`, { method:'DELETE' });

// Planned Services
export const fetchPlannedServices = (guestId)    => apiFetch(`/corporate-sessions/guests/${guestId}/planned-services`);
export const savePlannedService   = (guestId, d) => apiFetch(`/corporate-sessions/guests/${guestId}/planned-services`, { method:'POST', body:JSON.stringify(d) });
export const deletePlannedService = (serviceId)  => apiFetch(`/corporate-sessions/planned-services/${serviceId}`, { method:'DELETE' });

// Payment Requests
export const fetchSessionPaymentRequests  = (sessionId)    => apiFetch(`/corporate-sessions/${sessionId}/payment-requests`);
export const createSessionPaymentRequest  = (sessionId, d) => apiFetch(`/corporate-sessions/${sessionId}/payment-requests`, { method:'POST', body:JSON.stringify(d) });
export const updateSessionPaymentRequest  = (requestId, d) => apiFetch(`/corporate-sessions/payment-requests/${requestId}`, { method:'PUT', body:JSON.stringify(d) });

// Installments
export const fetchInstallments  = (requestId)    => apiFetch(`/corporate-sessions/payment-requests/${requestId}/installments`);
export const recordInstallment  = (requestId, d) => apiFetch(`/corporate-sessions/payment-requests/${requestId}/installments`, { method:'POST', body:JSON.stringify(d) });

// Check-In Gate
export const checkSessionCheckInGate = (reservationId) =>
  apiFetch(`/corporate-sessions/check-in-gate?reservationId=${encodeURIComponent(reservationId)}`);

// Proforma Data
export const fetchIndividualProformaData = (sessionGuestId) =>
  apiFetch(`/corporate-sessions/guests/${sessionGuestId}/proforma`);
export const fetchCorporateProformaData  = (sessionId) =>
  apiFetch(`/corporate-sessions/${sessionId}/corporate-proforma`);


// ─── Corporate Session API additions ─────────────────────────────────────────
// Add these exports to src/services/api.js (below the existing session block)
// They cover the new endpoints added in the backend refactor.

// Bulk enrol multiple PMS guests into a session in one call
export const bulkEnrolSessionGuests = (sessionId, guestIds) =>
  apiFetch(`/corporate-sessions/${sessionId}/guests/bulk`, {
    method: 'POST',
    body: JSON.stringify({ guestIds }),
  });

// Create tentative reservations for enrolled guests
// body: { roomTypeId?: string, guestIds?: string[] }
export const createSessionReservations = (sessionId, body = {}) =>
  apiFetch(`/corporate-sessions/${sessionId}/create-reservations`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

// Confirm all tentative reservations in a session
export const confirmSessionReservations = (sessionId) =>
  apiFetch(`/corporate-sessions/${sessionId}/confirm-reservations`, {
    method: 'POST',
    body: JSON.stringify({}),
  });

// Final corporate tax invoice data (post-checkout)
export const fetchCorporateSessionInvoiceData = (sessionId) =>
  apiFetch(`/corporate-sessions/${sessionId}/final-invoice`);